import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import ContentApi from "api/ContentApi";
import { normalizeLanguage } from "config/i18n";
import { getApiData } from "../../types/api";

export type ContentMap = Record<string, { title?: string; excerpt?: string; content?: string }>;

export interface ContentQueryProps {
  contents: ContentMap;
  contentsLoaded: boolean;
}

const EMPTY_CONTENTS: ContentMap = {};

export function contentQueryKey(language: string | null) {
  return ["content", language || "fr"];
}

export function useContent(language: string | null) {
  const normalizedLanguage = normalizeLanguage(language) || "fr";
  const query = useQuery({
    queryKey: contentQueryKey(normalizedLanguage),
    queryFn: () =>
      ContentApi.getAll(normalizedLanguage)
        .then((res) => getApiData(res, EMPTY_CONTENTS) as ContentMap)
        .catch(() => EMPTY_CONTENTS),
  });

  return {
    ...query,
    contents: query.data || EMPTY_CONTENTS,
    contentsLoaded: query.isSuccess,
  };
}

export function withContentData<P extends ContentQueryProps>(
  Component: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof ContentQueryProps>> {
  return function ContentData(props) {
    const { i18n } = useTranslation();
    const contentQuery = useContent(i18n.resolvedLanguage || null);

    return (
      <Component
        {...(props as P)}
        contents={contentQuery.contents}
        contentsLoaded={contentQuery.contentsLoaded}
      />
    );
  };
}
